import { PublicKey } from "@solana/web3.js";

// Configuration for the staking GUI
export const CONFIG = {
  // Program ID from environment or fallback
  PROGRAM_ID: new PublicKey(
    import.meta.env.VITE_PROGRAM_ID ||
      "2cQt5P1E3YyzwrGVrdK3Hf64hfBXJo5NHsg3DQXde6JN"
  ),

  // Energy Credits Program ID from environment or fallback
  EC_PROGRAM_ID: new PublicKey(
    import.meta.env.VITE_EC_PROGRAM_ID ||
      "5EhneM27LBwUmxUxgd6Fo6TDTJpKM5zBVGzBVAv6Qwoy"
  ),

  // Network settings from environment or fallback
  NETWORK: (import.meta.env.VITE_NETWORK || "devnet") as
    | "devnet"
    | "testnet"
    | "mainnet-beta"
    | "localnet",

  // RPC URL from environment or fallback
  RPC_URL: import.meta.env.VITE_RPC_URL || "http://127.0.0.1:8899",

  // Pass from env. Staking token mint address (hardcoded)
  STAKE_MINT: new PublicKey("FZhZSSQ6T9WnLzuC7fc9Mg1rbRUEkpjnCS7MFfwB7Z8n"),

  // Transaction settings
  TRANSACTION_OPTIONS: {
    commitment: "confirmed" as const,
    skipPreflight: false,
  },
};

// Helper function to get PDA addresses (matches offchain program structure)
export const getPDAAddresses = (programId: PublicKey) => {
  const getGlobalConfig = () => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("global_config")],
      programId
    )[0];
  };

  const getGlobalEpochState = () => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("global_epoch_state")],
      programId
    )[0];
  };

  const getUserState = (user: PublicKey, stakeMint: PublicKey) => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("user_state"), user.toBuffer(), stakeMint.toBuffer()],
      programId
    )[0];
  };

  const getEpoch = (epochIndex: number) => {
    const epochBytes = Buffer.alloc(8);
    epochBytes.writeBigUInt64LE(BigInt(epochIndex));
    return PublicKey.findProgramAddressSync(
      [Buffer.from("epoch"), epochBytes],
      programId
    )[0];
  };

  const getUserEpochPoints = (user: PublicKey, epochIndex: number) => {
    const epochBytes = Buffer.alloc(8);
    epochBytes.writeBigUInt64LE(BigInt(epochIndex));
    return PublicKey.findProgramAddressSync(
      [Buffer.from("user_epoch_points"), user.toBuffer(), epochBytes],
      programId
    )[0];
  };

  const getUserPoints = (user: PublicKey, stakeMint: PublicKey) => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("user_points"), user.toBuffer(), stakeMint.toBuffer()],
      programId
    )[0];
  };

  const getStakeVault = (stakeMint: PublicKey) => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("stake_vault"), stakeMint.toBuffer()],
      programId
    )[0];
  };

  const getProgramAuthority = () => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("program_authority")],
      programId
    )[0];
  };

  return {
    getGlobalConfig,
    getGlobalEpochState,
    getUserState,
    getEpoch,
    getUserEpochPoints,
    getUserPoints,
    getStakeVault,
    getProgramAuthority,
  };
};

// Helper function to get Energy Credits PDA addresses
export const getECPDAAddresses = (ecProgramId: PublicKey) => {
  const getECConfig = () => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("ec_config")],
      ecProgramId
    )[0];
  };

  const getECMint = () => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("ec_mint")],
      ecProgramId
    )[0];
  };

  const getMintAuthority = () => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from("mint_authority")],
      ecProgramId
    )[0];
  };

  return {
    getECConfig,
    getECMint,
    getMintAuthority,
  };
};
